/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/App.tsx
 * DESCRIPTION: Main application router with Clerk authentication,
 *              Admin authentication, and layout configuration. Handles
 *              authentication protection, route definitions, and overall
 *              application structure for the VeriMark Ai SaaS platform.
 *
 * ROUTES:
 * - /sign-in, /sign-up: Public authentication pages (Clerk)
 * - /dashboard: Main overview with stats and recent activity
 * - /analyze: File upload and analysis interface
 * - /library: Media library and identity management
 * - /identities/:id: File identity details (from library selection)
 * - /organizations: Multi-organization management
 * - /billing: Subscription plans and payment management
 * - /audit: API keys, webhooks, and activity logs
 * - /profile: User profile management
 * - /admin/*: Admin-only pages (Analytics, AI Providers, Users, Telemetry)
 *
 * ADMIN ROUTES (Password Protected):
 * - /admin/analytics: Intelligence analytics dashboard
 * - /admin/ai-providers: AI provider configuration (admin view)
 * - /admin/users: Individual user management (SaaS users)
 * - /admin/telemetry: Live telemetry & real-time intelligence
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2025-01-15
 * @updated 2026-03-27 - Added AdminAuthProvider, organized admin routes
 *                       under /admin/* with AdminLayout wrapper,
 *                       removed duplicate routes, added AdminTelemetry
 * ─────────────────────────────────────────────────────────────────────
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, RedirectToSignIn } from '@clerk/react';

// Context Providers
import { AdminAuthProvider } from './contexts/AdminAuthContext';

// Layout Components
import Layout from './components/Layout/Layout';
import AdminLayout from './components/Layout/AdminLayout';

// Page Components - User Pages
import Dashboard from './pages/Dashboard';
import FileAnalysis from './pages/FileAnalysis';
import Library from './pages/Library';
import Identities from './pages/Identities';
import Organizations from './pages/Organizations';
import Billing from './pages/Billing';
import AuditLogs from './pages/AuditLogs';
import UserProfile from './pages/UserProfile';

// Page Components - Admin Pages (under /admin/*)
import Analytics from './pages/Analytics';
import AIProviders from './pages/AIProviders';
import AdminUsers from './pages/AdminUsers';
import AdminTelemetry from './pages/AdminTelemetry';
import IntelTelemetry from './pages/IntelTelemetry';

/**
 * ProtectedRoute Component
 * Ensures user is authenticated via Clerk before accessing routes.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} Protected content or sign-in redirect
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Use Layout component for consistent sidebar + content structure
  return <Layout>{children}</Layout>;
};

/**
 * PublicRoute Component
 * Redirects to dashboard if user is already signed in.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} Public content or dashboard redirect
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * App Component
 * Root component with routing, Clerk authentication, and Admin authentication.
 *
 * @returns {JSX.Element} Application with routing
 */
function App() {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <Routes>
          {/* ───────────────────────────────────────────────────────────
              PUBLIC ROUTES - Authentication Pages
              ─────────────────────────────────────────────────────────── */}
          <Route
            path="/sign-in/*"
            element={
              <PublicRoute>
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  {/* Clerk's SignIn component renders here via ClerkProvider in main.tsx */}
                </div>
              </PublicRoute>
            }
          />
          
          <Route
            path="/sign-up/*"
            element={
              <PublicRoute>
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  {/* Clerk's SignUp component renders here via ClerkProvider in main.tsx */}
                </div>
              </PublicRoute>
            }
          />

          {/* ───────────────────────────────────────────────────────────
              PROTECTED USER ROUTES - Wrapped with Layout (Sidebar + Header)
              ─────────────────────────────────────────────────────────── */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />

          <Route path="/analyze" element={
            <ProtectedRoute>
              <FileAnalysis />
            </ProtectedRoute>
          } />

          <Route path="/library" element={
            <ProtectedRoute>
              <Library />
            </ProtectedRoute>
          } />

          {/* Redirect /identities to library (selection handled via navigation) */}
          <Route path="/identities" element={
            <ProtectedRoute>
              <Navigate to="/library" replace />
            </ProtectedRoute>
          } />

          {/* File identity details - accessed from library selection */}
          <Route path="/identities/:id" element={
            <ProtectedRoute>
              <Identities />
            </ProtectedRoute>
          } />

          <Route path="/organizations" element={
            <ProtectedRoute>
              <Organizations />
            </ProtectedRoute>
          } />

          <Route path="/billing" element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          } />

          <Route path="/audit" element={
            <ProtectedRoute>
              <AuditLogs />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          } />

          {/* TEMPORARY INTEL DASHBOARD */}
          <Route path="/intel" element={
            <ProtectedRoute>
              <IntelTelemetry />
            </ProtectedRoute>
          } />

          {/* ───────────────────────────────────────────────────────────
              ADMIN ROUTES - Password Protected via AdminLayout
              All admin pages require:
              1. Clerk authentication (ProtectedRoute)
              2. Admin password authentication (AdminLayout)
              3. Business/Enterprise license (page-level check)
              ─────────────────────────────────────────────────────────── */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout 
                title="Admin Dashboard" 
                description="System administration, analytics, and user management"
              >
                <Navigate to="/admin/analytics" replace />
              </AdminLayout>
            </ProtectedRoute>
          }>
            {/* Analytics - Intelligence dashboard */}
            <Route path="analytics" element={<Analytics />} />
            
            {/* AI Providers - Admin configuration view */}
            <Route path="ai-providers" element={<AIProviders />} />
            
            {/* User Management - Manage individual SaaS users */}
            <Route path="users" element={<AdminUsers />} />
            
            {/* Telemetry - Live real-time intelligence */}
            <Route path="telemetry" element={<AdminTelemetry />} />
          </Route>

          {/* ───────────────────────────────────────────────────────────
              CATCH-ALL & FALLBACK ROUTES
              ─────────────────────────────────────────────────────────── */}
          
          {/* Root redirect to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Fallback for unknown routes - redirect to dashboard */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AdminAuthProvider>
    </BrowserRouter>
  );
}

export default App;
